import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

export default function AdminDashboard() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const pendingRes = await axios.get(`${API_URL}/admin/pending-approvals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(pendingRes.data);

      const approvedRes = await axios.get(`${API_URL}/admin/approved-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovedUsers(approvedRes.data);

      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch users. Make sure you are logged in as admin.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${API_URL}/admin/approve/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('User approved!');
      await fetchData();
    } catch (err) {
      alert('Failed to approve user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleReject = async (userId) => {
    if (window.confirm('Are you sure you want to reject this user?')) {
      try {
        await axios.post(`${API_URL}/admin/reject/${userId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('User rejected!');
        await fetchData();
      } catch (err) {
        alert('Failed to reject user: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Admin Dashboard - User Approvals</h1>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          ← Back to Dashboard
        </button>
      </div>
      
      {error && (
        <div style={{ backgroundColor: '#ffcccc', border: '1px solid #cc0000', color: '#cc0000', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '20px', borderBottom: '2px solid #ddd' }}>
        <button onClick={() => setActiveTab('pending')} style={{ padding: '10px 20px', marginRight: '10px', backgroundColor: activeTab === 'pending' ? '#1f3864' : '#f0f0f0', color: activeTab === 'pending' ? 'white' : 'black', border: 'none', borderRadius: '5px 5px 0 0', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
          Pending Approvals ({pendingUsers.length})
        </button>
        <button onClick={() => setActiveTab('approved')} style={{ padding: '10px 20px', backgroundColor: activeTab === 'approved' ? '#1f3864' : '#f0f0f0', color: activeTab === 'approved' ? 'white' : 'black', border: 'none', borderRadius: '5px 5px 0 0', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
          Approved Users ({approvedUsers.length})
        </button>
      </div>

      {activeTab === 'pending' && (
        <div>
          <h2>Pending User Approvals</h2>
          {pendingUsers.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>No pending approvals</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Signup Date</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{user.email}</td>
                    <td style={{ padding: '12px' }}>{user.full_name || '—'}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#666' }}>{formatDate(user.created_at)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button onClick={() => handleApprove(user.id)} style={{ padding: '8px 16px', marginRight: '8px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                        ✓ Approve
                      </button>
                      <button onClick={() => handleReject(user.id)} style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                        ✕ Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'approved' && (
        <div>
          <h2>Approved Users</h2>
          {approvedUsers.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>No approved users</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Signup Date</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {approvedUsers.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{user.email}</td>
                    <td style={{ padding: '12px' }}>{user.full_name || '—'}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#666' }}>{formatDate(user.created_at)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button onClick={() => handleReject(user.id)} style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                        Remove Approval
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
